import { InfoCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import * as React from 'react'
import { clsx } from 'clsx'

import { type Repository } from '@basehub/data/replicache/index'

import { Flip } from '~/lib/gsap'
import { Button } from '~/sotano-kit/primitives/button'
import { LabeledCheckbox } from '~/sotano-kit/primitives/checkbox'
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import { LabeledInput } from '~/sotano-kit/primitives/input'
import { usePersonalTeamAndRepos } from '~/hooks-data/use-personal-team-and-repos'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'

import { BaseHubAvatar } from '~/components/basehub-avatar'
import { getDefaultAvatarSrc } from '@basehub/data/auth/utils/avatar'
import { useUserStore } from '~/app/_store/user-store'
import { useGsapContext } from '~/hooks/use-gsap-context'
import { queryMatches } from '~/lib/simple-input-search'

import type { OnboardingStep } from '.'

export const TeamOnboardingRepoTransfer: React.FC<
  Omit<OnboardingStep, 'onNextStep'> & {
    preselectedRepos: Array<Repository['id']>
    focusInput: boolean
    onNextStep: (
      e: React.MouseEvent,
      selectedRepos: Array<Repository['id']>
    ) => void
  }
> = ({ focusInput, onNextStep, preselectedRepos }) => {
  const { team } = useSelectedTeam()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const { team: personalTeam, repositories } = usePersonalTeamAndRepos()

  const [reposToTransfer, setReposToTransfer] =
    React.useState<Array<Repository['id']>>(preselectedRepos)
  const [repoSearch, setRepoSearch] = React.useState('')

  const flipStateRef = React.useRef<any>(undefined)
  const repoListRef = React.useRef<HTMLUListElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const filteredRepos = React.useMemo(
    () =>
      (repositories ?? []).filter(
        (repo) =>
          queryMatches(repoSearch, repo?.title) ||
          queryMatches(repoSearch, repo?.id) ||
          queryMatches(repoSearch, repo?.slug)
      ),
    [repoSearch, repositories]
  )

  React.useEffect(() => {
    if (!focusInput) return
    inputRef.current?.focus()
  }, [focusInput])

  useGsapContext(() => {
    return () => {
      if (repoListRef?.current) {
        repoListRef.current.style.overflowY = 'hidden'
      }
      if (flipStateRef.current) {
        Flip.from(flipStateRef.current, {
          duration: 0.18,
          ease: 'power1.inOut',
          onComplete: () => {
            if (repoListRef?.current) {
              repoListRef.current.style.overflowY = 'auto'
            }
          },
        })
      }
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRepos.length])

  return (
    <div
      className={clsx(
        {
          'md:h-[607px]': (repositories ?? []).length > 3,
          'md:h-[573px]': (repositories ?? []).length <= 2,
        },
        'flex flex-col justify-between'
      )}
    >
      <div>
        <DialogHeader className="pt-4 px-4 !space-y-3">
          <DialogTitle>Transfer</DialogTitle>
        </DialogHeader>
        <p className="text-dark-muted leading-[1.4] px-4 pt-1.5 pb-6">
          Do you wish to transfer any repositories from your Personal Account
          to&nbsp;{team?.title}?
        </p>

        <LabeledInput
          className="!pt-0 !px-4 !-mx-0 !pb-6"
          label="Team Name"
          blocked={false}
          autoFocus={focusInput}
          name="title"
          ref={inputRef}
          inputProps={{
            onChange: (e) => {
              if (repoListRef.current) {
                flipStateRef.current = Flip.getState(repoListRef.current, {
                  props: 'height',
                })
              }
              setRepoSearch(e.currentTarget.value)
            },
            value: repoSearch,
            autoComplete: 'off',
            placeholder: 'Search repos...',
            icon: <MagnifyingGlassIcon className="text-dark-muted" />,
          }}
        />

        <div className="px-4">
          <div className="rounded-md border border-dark-border-solid bg-dark-base-dark">
            <LabeledCheckbox
              className="px-3 py-2 border-b border-dark-border-solid tabular-nums [&>span]pl-3"
              checkboxProps={{
                className: 'scroll-my-[22px]',
                checked:
                  filteredRepos.length < 1
                    ? false
                    : reposToTransfer.length >= filteredRepos?.length,
                onCheckedChange: (checked) => {
                  if (filteredRepos.length < 1) return
                  return setReposToTransfer(
                    checked ? (filteredRepos?.map(({ id }) => id) ?? []) : []
                  )
                },
              }}
              label={
                reposToTransfer.length >= filteredRepos?.length
                  ? 'All repos selected'
                  : `${reposToTransfer.length} repo${
                      reposToTransfer.length !== 1 ? 's' : ''
                    } selected`
              }
              name="transfer-all-repos"
              id="transfer-all-repos"
              horizontal="reverse"
            />
            <ul ref={repoListRef} className="max-h-[217px] overflow-auto">
              {!filteredRepos?.length ? (
                <li className="text-sm leading-normal px-3 py-2 text-dark-muted">
                  No results
                </li>
              ) : (
                filteredRepos?.map((repo, index) => (
                  <li key={repo.id}>
                    <LabeledCheckbox
                      className={clsx(
                        'px-3 py-2 [&>span]:min-w-0 [&>span]:!pl-3 overflow-hidden',
                        index < filteredRepos.length - 1 &&
                          'border-b border-dark-border-solid'
                      )}
                      checkboxProps={{
                        className: 'scroll-my-[22px]',
                        checked: reposToTransfer.includes(repo.id),
                        onCheckedChange: (checked) => {
                          return setReposToTransfer((prevState) => {
                            if (!checked) {
                              return prevState.filter(
                                (repoId) => repoId !== repo.id
                              )
                            }

                            const newState = [...prevState]
                            newState.push(repo.id)
                            return newState
                          })
                        },
                      }}
                      label={
                        <div className="flex items-center space-x-3">
                          <BaseHubAvatar
                            name={personalTeam?.title ?? ''}
                            id={personalTeam?.id ?? ''}
                            size="small"
                            className="h-6 w-6 rounded-full bg-[#323232]"
                            forceSrc={
                              personalTeam?.plan === 'personal' ||
                              personalTeam?.plan_v2?.type === 'personal'
                                ? (optimisticUser?.imageUrl ??
                                  getDefaultAvatarSrc(optimisticUser?.id ?? ''))
                                : (personalTeam?.avatarUrl ??
                                  getDefaultAvatarSrc(personalTeam?.id ?? ''))
                            }
                          />
                          <div className="overflow-hidden">
                            <p className="text-dark-bright mb-0.5">
                              {repo.title}
                            </p>
                            <p className="text-ellipsis overflow-hidden whitespace-nowrap">
                              basehub.com/{personalTeam?.slug}/{repo.slug}
                            </p>
                          </div>
                        </div>
                      }
                      name={`repo-${repo.id}-transfer`}
                      id={`repo-${repo.id}`}
                      horizontal="reverse"
                    />
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      <div>
        <div className="text-dark-muted px-4 pb-4 flex items-start space-x-2 text-sm leading-normal mt-6">
          <InfoCircledIcon className="flex-shrink-0 mt-1" />{' '}
          <p>
            By clicking “continue”, selected repositories will be migrated to
            your new team upon completing the&nbsp;onboarding.
          </p>
        </div>
        <DialogFooter className="border-t border-dark-border-solid px-4 py-4 sm:space-x-4 flex">
          <Button
            size="large"
            intent="secondary"
            onClick={(e) => onNextStep(e, reposToTransfer)}
          >
            Continue
          </Button>
        </DialogFooter>
      </div>
    </div>
  )
}
